import { logAPIError } from '../../utils/errorLogger'
import type {
  FarmProfile,
  FarmProfileUpdateRequest,
  FarmAvailability,
  FarmAvailabilityUpdateRequest,
  FarmPricing,
  FarmPricingUpdateRequest,
  FarmVerification
} from '../types'

/**
 * Type signature for the makeRequest method from BackendService
 */
export type MakeRequest = <T>(endpoint: string, options?: RequestInit) => Promise<T>

/**
 * Get the authenticated farm's profile
 */
export async function getFarmProfile(makeRequest: MakeRequest): Promise<FarmProfile> {
  try {
    const response = await makeRequest<{ success: boolean; profile: FarmProfile }>('/api/farms/profile', {
      method: 'GET'
    })
    return response.profile
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load farm profile'
    logAPIError('/api/farms/profile', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Update the authenticated farm's profile
 */
export async function updateFarmProfile(
  makeRequest: MakeRequest,
  profile: FarmProfileUpdateRequest
): Promise<FarmProfile> {
  try {
    const response = await makeRequest<{ success: boolean; message: string; profile: FarmProfile }>('/api/farms/profile', {
      method: 'PUT',
      body: JSON.stringify({
        phone: profile.phone,
        website: profile.website,
        description: profile.description,
        logo_url: profile.logoUrl,
        address: profile.address
          ? {
              line1: profile.address.line1,
              line2: profile.address.line2,
              city: profile.address.city,
              state_province: profile.address.stateProvince,
              postal_code: profile.address.postalCode,
              country: profile.address.country
            }
          : undefined,
        service_radius_km: profile.serviceRadiusKm,
        average_turnaround_days: profile.averageTurnaroundDays,
        ships_nationally: profile.shipsNationally,
        ships_internationally: profile.shipsInternationally,
        printer_count: profile.printerCount,
        printer_models: profile.printerModels,
        materials_available: profile.materialsAvailable,
        max_build_volume: profile.maxBuildVolume,
        layer_heights: profile.layerHeights,
        certifications: profile.certifications,
        medical_grade_capable: profile.medicalGradeCapable,
        biocompatible_materials: profile.biocompatibleMaterials,
        clean_room_available: profile.cleanRoomAvailable,
        sterilization_capable: profile.sterilizationCapable
      })
    })

    return response.profile
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update farm profile'
    logAPIError('/api/farms/profile', 'PUT', 0, errorMessage, profile)
    throw error
  }
}

/**
 * Update the authenticated farm's availability
 */
export async function updateFarmAvailability(
  makeRequest: MakeRequest,
  availability: FarmAvailabilityUpdateRequest
): Promise<FarmAvailability> {
  try {
    const response = await makeRequest<{ success: boolean; message: string; availability: FarmAvailability }>('/api/farms/availability', {
      method: 'PUT',
      body: JSON.stringify({
        operating_hours: availability.operatingHours,
        vacation_mode: availability.vacationMode,
        vacation_start: availability.vacationStart,
        vacation_end: availability.vacationEnd,
        vacation_message: availability.vacationMessage
      })
    })

    return response.availability
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update farm availability'
    logAPIError('/api/farms/availability', 'PUT', 0, errorMessage, availability)
    throw error
  }
}

/**
 * Get the authenticated farm's pricing configuration
 */
export async function getFarmPricing(makeRequest: MakeRequest): Promise<FarmPricing> {
  try {
    const response = await makeRequest<{ success: boolean; pricing: FarmPricing }>('/api/farms/pricing', {
      method: 'GET'
    })
    return response.pricing
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load farm pricing'
    logAPIError('/api/farms/pricing', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Update the authenticated farm's pricing configuration
 */
export async function updateFarmPricing(
  makeRequest: MakeRequest,
  pricing: FarmPricingUpdateRequest
): Promise<FarmPricing> {
  try {
    const response = await makeRequest<{ success: boolean; message: string; pricing: FarmPricing }>('/api/farms/pricing', {
      method: 'PUT',
      body: JSON.stringify({
        minimum_order_value: pricing.minimumOrderValue,
        rush_order_available: pricing.rushOrderAvailable,
        rush_order_multiplier: pricing.rushOrderMultiplier,
        bulk_discount_available: pricing.bulkDiscountAvailable,
        accepts_custom_materials: pricing.acceptsCustomMaterials
      })
    })

    return response.pricing
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update farm pricing'
    logAPIError('/api/farms/pricing', 'PUT', 0, errorMessage, pricing)
    throw error
  }
}

/**
 * Upload a verification document for the authenticated farm
 */
export async function uploadFarmVerificationDocument(
  baseURL: string,
  authToken: string | null,
  file: File,
  documentType: string,
  notes?: string
): Promise<FarmVerification> {
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('document_type', documentType)
    if (notes) {
      formData.append('notes', notes)
    }

    const headers: Record<string, string> = {}
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`
    }

    const response = await fetch(`${baseURL}/api/farms/verification-documents`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    })

    if (!response.ok) {
      let errorMessage = 'Failed to upload verification document'

      try {
        const errorData = await response.json() as { detail?: string }
        if (errorData.detail) {
          errorMessage = errorData.detail
        }
      } catch {
        // Ignore response parsing errors and keep the default message.
      }

      throw new Error(errorMessage)
    }

    const result = await response.json() as { success: boolean; message: string; verification: FarmVerification }
    return result.verification
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to upload verification document'
    logAPIError('/api/farms/verification-documents', 'POST', 0, errorMessage, { documentType, fileName: file.name })
    throw error
  }
}
